<template>
  <div
    ref="containerRef"
    class="relative w-full transition-opacity duration-200 ease-out h-full flex flex-col overflow-hidden"
    data-test-id="preview-list-container"
  >
    <div
      ref="listRef"
      :class="[
        'preview-list w-full relative flex-1 bg-secondary-background',
        fullPageHeightScroll ? 'overflow-y-hidden' : 'overflow-y-auto',
      ]"
      v-hide-keyboard-scroll
      @scroll="onScroll($event)"
    >
      <div v-if="refreshing" class="w-full flex justify-center py-5">
        <Spinner :size="40" />
      </div>

      <div class="grid w-full bg-secondary-background" ref="innerListRef" :style="gridStyle">
        <template v-for="(item, index) in items" :key="item.id">
          <PreviewEntry
            :data-index="index"
            :class="{ 'opacity-50': itemMovedSet.has(item.id) }"
            :item="item"
            :window-width="screenWidth"
            :line-builder="lineBuilder"
            :hide-add-to-collection="hideAddToCollection"
            :view-mode="listViewMode"
            :novelty-add-mode="noveltyAddMode"
            @addToCollection="(i) => onOpenBulkEdit(i, true)"
            @toggleOwned="(i, owned) => toggleOwned(i, owned)"
            @toggleRead="(i, read) => toggleRead(i, read)"
            @increaseOwned="(i) => onIncreaseOwned(i)"
            @increaseRead="(i) => onIncreaseRead(i)"
            @openBulkEdit="(i) => onOpenBulkEdit(i)"
            @openDetails="(i) => emit('openDetails', i)"
            @openDetailsNewTab="(i) => emit('openDetailsNewTab', i)"
          />
        </template>
      </div>
      <div ref="paginationSentinelRef" class="w-full h-50 -mt-50 pointer-events-none" />
      <component class="mx-7.5 mt-7.5" v-if="noItems" :is="noItemsComponent ?? NoResults" />
    </div>

    <AniRefresher v-if="!disableRefresh" :scroll-top="scrollY" @refresh="onRefresh" />

    <CollectionBulkEditModal
      v-model:visible="bulkEditState.visible"
      :header="
        bulkEditState.collectionEntry
          ? 'bulkEditExistingCollectionTitle'
          : 'bulkEditNewCollectionTitle'
      "
      :entry-id="bulkEditState.entryId"
      :collection-entry="bulkEditState.collectionEntry"
      :preselected-versions="bulkEditState.preselectedVersions"
      :preselected-icon="bulkEditState.preselectedIcon"
      :selected-collection-id="bulkEditState.selectedCollectionId"
      :default-selected-tab="bulkEditState.defaultSelectedTab"
      @dismiss="closeModal"
      @collectionSaved="closeModal"
      @collectionDeleted="closeModal"
    ></CollectionBulkEditModal>

    <CollectionBundleAddModal
      v-model:visible="bundleAddState.visible"
      :releases="bundleAddState.releases"
      :is-bundle="bundleAddState.isBundle"
      @confirm="onBundleAddConfirm"
    ></CollectionBundleAddModal>

    <transition name="fade">
      <div
        v-if="isLoading"
        :class="
          items.length === 0
            ? 'absolute inset-0 flex items-center justify-center z-5 bg-secondary-background'
            : 'absolute bottom-21.25 left-0 right-0 flex justify-center z-5'
        "
      >
        <Spinner :size="40" />
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="scrollGrip.visible"
        class="absolute top-0 right-0 w-0.75 bg-grey-1 transition-opacity duration-300 ease-out"
        :style="scrollGrip.style"
      ></div>
    </transition>
  </div>
</template>

<script setup lang="ts" generic="T extends DocumentNode">
import { Device } from '@capacitor/device'
import {
  computed,
  nextTick,
  onActivated,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  toRefs,
  watch,
} from 'vue'
import PreviewEntry, { type PreviewEntryData } from '@/components/PreviewEntry.vue'
import {
  BulkEditModalProps,
  BulkModalCollectionEntry,
  CollectionBulkEditTab,
  formatVolumeNumber,
  getSuffixFromVolumeVersion,
  getVersionFromVolume,
  getVolumeNumberFromVersion,
  type PreviewListProps,
  responsive,
  addVolumesFromCollectionToOwnedMap,
  formatVolumeRange,
  usePagination,
  getYear,
  getMonth,
} from '@/utils'
import { showToast } from '@/utils/showToast'
import { useLocalizationStore } from '@/stores/localization.ts'
import { useThemeStore } from '@/stores/theme.ts'
import { isEntryStatusComplete, useUserCollectionStore } from '@/stores/user-collection'
import { storeToRefs } from 'pinia'
import { useWindowSize } from '@vueuse/core'
import type { DocumentNode } from '@/vendor/apollo-client'
import CollectionBulkEditModal from '@/components/CollectionBulkEditModal.vue'
import CollectionBundleAddModal, {
  type BundleAddRelease,
} from '@/components/CollectionBundleAddModal.vue'
import NoResults from '@/components/NoResults.vue'
import { useUiStore } from '@/stores/ui.store.ts'
import { useAdmobStore } from '@/stores/admob.ts'
import { match, P } from 'ts-pattern'
import {
  NoveltyRelationTypeType,
  OrderDirection,
  UserMiscPossessionActivityTypeType,
  UserReleaseEntryStatusCollectionStatusType,
  UserReleaseEntryStatusCurrentReadStatusType,
} from '../../__generated__/graphql.ts'
import { GET_RELEASE_ENTRY_WITH_SERIES_INFO, GET_RELEASE_ITEM_COLLECTIONS } from '@/queries'
import { catalogQuery } from '@/data/catalogClient.ts'
import Spinner from '@/components/Spinner.vue'
import AniRefresher from '@/components/AniRefresher.vue'
import { useInfoStore } from '@/stores/info.ts'
import { useScrollGrip } from '@/utils/useScrollGrip.ts'
import { usePreferencesStore } from '@/stores/preferences.ts'
import vHideKeyboardScroll from '@/directives/hide-keyboard-scroll.ts'

const props = withDefaults(defineProps<PreviewListProps<T>>(), {
  showScrollOnStart: false,
  disableCache: false,
  itemMovedCheck: () => false,
})
const { increaseAdScore } = useAdmobStore()
const { listViewMode } = storeToRefs(usePreferencesStore())

async function closeModal() {
  bulkEditState.visible = false
  await increaseAdScore()
}

const emit = defineEmits<{
  (e: 'openDetails', item: PreviewEntryData): void
  (e: 'openDetailsNewTab', item: PreviewEntryData): void
}>()

const {
  maxItemsToLoad,
  releaseItemFilter,
  sortParams,
  getWhere,
  getSort,
  noItemsComponent,
  disableCache,
  itemMovedCheck,
  fullPageHeightScroll,
  lineBuilder,
  hideAddToCollection,
  disableRefresh,
  hideTopPadding,
  noveltyAddMode,
  reserveFabSpace,
} = toRefs(props)

const containerRef = ref<HTMLElement | null>(null)
const listRef = ref<HTMLElement | null>(null)
const innerListRef = ref<HTMLElement | null>(null)
const paginationSentinelRef = ref<HTMLElement | null>(null)
const refreshing = ref(false)
const restoringScroll = ref(false)
const scrollY = ref(0)
const imageHeight = ref(0)
const { width: screenWidth, height: screenHeight } = useWindowSize()

const scrollGrip = reactive(
  useScrollGrip(listRef, innerListRef, {
    showOnStart: props.showScrollOnStart,
  }),
)

const { t, getVolumeSuffixFromId } = useLocalizationStore()
const { currentTheme } = storeToRefs(useThemeStore())
const { collectionMap } = storeToRefs(useUserCollectionStore())
const { openOptionsSelect } = useUiStore()
const { insets } = useInfoStore()
const {
  updateOwned,
  updateOwnedMap,
  updateMiscOwned,
  createCollectionEntry,
  getLatestNotOwnedVolumeVersions,
  getLatestNotReadVolumeNo,
  updateLatestRead,
} = useUserCollectionStore()
const { width } = useWindowSize()

const {
  items,
  loadNextPage,
  loadUntilSkip,
  currentSkip,
  isLoading,
  noItems,
  resetPagination,
  updateConfig,
  firstLoadDone,
} = usePagination({
  query: props.query,
  maxItemsToLoad: maxItemsToLoad.value,
  takeNumber: listViewMode.value !== 'gallery' ? 50 : screenWidth.value > 600 ? 24 : 8,
  getPaginationData: props.getPaginationData,
  initialVariables: {
    ...getWhere.value(releaseItemFilter.value),
    ...getSort.value(sortParams.value),
  },
})

let paginationObserver: IntersectionObserver | null = null

onMounted(() => {
  paginationObserver = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting && !isLoading.value && !restoringScroll.value) {
        loadNextPage()
      }
    },
    { rootMargin: '0px 0px 0px 0px' },
  )
})

watch(paginationSentinelRef, (el, oldEl) => {
  if (oldEl && paginationObserver) {
    paginationObserver.unobserve(oldEl)
  }
  if (el && paginationObserver) {
    paginationObserver.observe(el)
  }
})

watch(
  () => items.value.length,
  () => {
    const el = paginationSentinelRef.value
    if (el && paginationObserver) {
      paginationObserver.unobserve(el)
      paginationObserver.observe(el)
    }
  },
)

onUnmounted(() => {
  paginationObserver?.disconnect()
  paginationObserver = null
})

onActivated(() => {
  listRef.value?.scrollTo(0, scrollY.value)
})

let lastResetKey = JSON.stringify({
  ...getWhere.value(releaseItemFilter.value),
  ...getSort.value(sortParams.value),
})
let lastQueryRef = props.query

const itemMovedSet = computed(() => {
  const wasMoved = new Set<string>()
  items.value.forEach((item) => {
    if (itemMovedCheck.value(item)) {
      wasMoved.add(item.id)
    }
  })
  return wasMoved
})

watch([releaseItemFilter, sortParams, () => props.query], () => {
  const newVars = {
    ...getWhere.value(releaseItemFilter.value),
    ...getSort.value(sortParams.value),
  }
  const newKey = JSON.stringify(newVars)
  const queryChanged = props.query !== lastQueryRef

  if (newKey === lastResetKey && !queryChanged) {
    return
  }

  lastResetKey = newKey
  lastQueryRef = props.query
  updateConfig(props.query, props.getPaginationData)
  resetPagination(newVars)
})

onMounted(() => {
  if (disableCache.value) {
    resetPagination({
      ...getWhere.value(releaseItemFilter.value),
      ...getSort.value(sortParams.value),
    })
  }
})

type BulkEditState = BulkEditModalProps & {
  visible: boolean
}

const bulkEditState = reactive<BulkEditState>({
  visible: false,
  header: 'bulkEditExistingCollectionTitle',
  collectionEntry: undefined as BulkModalCollectionEntry | undefined,
  preselectedVersions: undefined as string[] | undefined,
  selectedCollectionId: undefined as string | undefined,
  defaultSelectedTab: 'volume' as CollectionBulkEditTab,
  entryId: '',
})

type BundleEntryGroup = {
  entryId: string
  title: string
  versions: string[]
  numbers: string[]
}

const bundleAddState = reactive<{
  visible: boolean
  item: PreviewEntryData | null
  releases: BundleAddRelease[]
  entries: BundleEntryGroup[]
  collectionId: string | null
  isBundle: boolean
}>({
  visible: false,
  item: null,
  releases: [],
  entries: [],
  collectionId: null,
  isBundle: false,
})

watch(items.value, async () => {
  if (items.value.length === 0 || scrollGrip.visible) return
  await nextTick()
  scrollGrip.recalculate()
  scrollGrip.showAndScheduleHide()
})

const floatingActionButtonClearance = 50 + 32

const gridStyle = computed(() => {
  const paddingTop = `${hideTopPadding.value ? 0 : currentTheme.value.layout.appPadding}px`
  const bottomFabClearance = reserveFabSpace.value ? floatingActionButtonClearance : 0
  const padding =
    items.value.length > 0
      ? {
          padding: `${currentTheme.value.layout.appPadding}px`,
          paddingTop,
          paddingBottom: `${currentTheme.value.layout.appPadding + insets.bottom + bottomFabClearance}px`,
        }
      : { padding: 0 }
  if (listViewMode.value === 'compact') {
    return {
      gridTemplateColumns: '1fr',
      gap: '10px',
      ...padding,
    }
  }
  if (listViewMode.value === 'list') {
    return {
      gridTemplateColumns: '1fr',
      gap: '10px',
      ...padding,
    }
  }
  const columns = responsive(screenWidth.value, 2, 2, 3, 5)
  return {
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: `${currentTheme.value.layout.releaseItemGapHorizontal}px`,
    ...padding,
  }
})

const platform = ref('unknown')
onMounted(async () => {
  const info = await Device.getInfo()
  platform.value = info.platform
})

const resetBulkEditState = () => {
  bulkEditState.visible = false
  bulkEditState.collectionEntry = undefined
  bulkEditState.entryId = ''
  bulkEditState.preselectedVersions = undefined
  bulkEditState.preselectedIcon = undefined
  bulkEditState.selectedCollectionId = undefined
  bulkEditState.defaultSelectedTab = 'volume'
}

const groupBundleReleaseItems = (
  releaseItems: {
    entryId?: string | null
    number?: string | null
    volumeSuffix?: string | null
    localizedSeriesTitle?: string | null
  }[],
): BundleEntryGroup[] => {
  const byEntry = new Map<string, BundleEntryGroup>()
  for (const releaseItem of releaseItems) {
    const entryId = releaseItem.entryId
    if (!entryId) continue
    const group = byEntry.get(entryId) ?? {
      entryId,
      title: releaseItem.localizedSeriesTitle ?? '',
      versions: [],
      numbers: [],
    }
    group.versions.push(getVersionFromVolume(releaseItem))
    if (releaseItem.number) group.numbers.push(releaseItem.number)
    byEntry.set(entryId, group)
  }
  return [...byEntry.values()]
}

const buildBundleAddReleases = (groups: BundleEntryGroup[]): BundleAddRelease[] =>
  groups.map((group) => ({
    id: group.entryId,
    label: t('collectionRangeLabelWithTitle', {
      title: group.title,
      range: formatVolumeRange(group.numbers),
    }),
  }))

const openBundleAddModal = async (item: PreviewEntryData) => {
  const result = await catalogQuery({
    query: GET_RELEASE_ITEM_COLLECTIONS,
    variables: {
      where: { id: { equals: item.relationId } },
      orderBy: { releaseDate: OrderDirection.Asc },
    },
  })
  const collection = result.data?.releaseItemCollections?.[0]
  const groups = groupBundleReleaseItems(collection?.releaseItems ?? [])
  bundleAddState.item = item
  bundleAddState.entries = groups
  bundleAddState.collectionId = item.relationId
  bundleAddState.releases = buildBundleAddReleases(groups)
  bundleAddState.isBundle = collection?.name === 'bundle'
  bundleAddState.visible = true
}

const addReleaseItemsToEntry = async (
  entryId: string,
  versions: string[],
  collectionId?: string,
  suppressSiblingToast?: boolean,
) => {
  const ownedMapForEntry = versions.reduce<Record<string, number>>((map, version) => {
    map[version] = 1
    return map
  }, {})

  if (collectionMap.value?.[entryId]) {
    if (versions.length > 0) {
      updateOwnedMap(entryId, ownedMapForEntry)
    }
    if (collectionId) {
      updateMiscOwned(entryId, collectionId, UserMiscPossessionActivityTypeType.Collection, 1, {
        suppressSiblingToast,
      })
    }
    return
  }

  const result = await catalogQuery({
    query: GET_RELEASE_ENTRY_WITH_SERIES_INFO,
    variables: { where: { id: entryId } },
  })
  const entry = result.data?.releaseEntry
  if (!entry) return

  createCollectionEntry({
    collectionId: null,
    readId: null,
    entryId,
    ownedMap: ownedMapForEntry,
    miscOwnedMap: collectionId
      ? { [collectionId]: { type: UserMiscPossessionActivityTypeType.Collection, quantity: 1 } }
      : {},
    latestReadMap: {},
    collectionStatus: UserReleaseEntryStatusCollectionStatusType.Collecting,
    currentReadStatus: UserReleaseEntryStatusCurrentReadStatusType.Planned,
    sortedVolumeNumbers: entry.sortedVolumeNumbers ?? [],
    notReleasedVolumeNumbers: entry.notReleasedVolumeNumbers ?? [],
    entryCompleted: isEntryStatusComplete(entry.status),
    startYear: getYear(entry.startDate),
    startMonth: getMonth(entry.startDate),
    endYear: getYear(entry.endDate),
    endMonth: getMonth(entry.endDate),
    suppressSiblingToast,
  })
}

const onBundleAddConfirm = async () => {
  const item = bundleAddState.item
  if (!item) return
  // Twin collections are kept in sync server-side: setting the misc owned on the
  // source entry makes syncTwinMisc mirror the bundle (and each twin's own
  // volumes) onto the sibling entries. Writing the siblings here would use the
  // source collection id on entries whose twin has a different id and fight that
  // propagation, so only the source entry is touched.
  const sourceGroup = bundleAddState.entries.find((group) => group.entryId === item.entryId)
  await addReleaseItemsToEntry(
    item.entryId,
    sourceGroup?.versions ?? [],
    bundleAddState.collectionId ?? undefined,
    true,
  )
}

const isSpanningCollection = (item: PreviewEntryData) =>
  item.relationType === NoveltyRelationTypeType.Collection && item.collectionSpansEntries === true

const onOpenBulkEdit = async (item: PreviewEntryData, add_action?: boolean) => {
  if (isSpanningCollection(item)) {
    if (add_action) {
      await openBundleAddModal(item)
    } else {
      showToast({ text: t('bulkEditUnavailableForBundle') })
    }
    return
  }

  const collection = collectionMap.value?.[item.entryId]
  resetBulkEditState()
  bulkEditState.collectionEntry = collection as BulkModalCollectionEntry
  bulkEditState.entryId = item.entryId

  await match(item.relationType)
    .with(NoveltyRelationTypeType.Collection, async () => {
      bulkEditState.defaultSelectedTab = 'collection'
      if (add_action) {
        bulkEditState.selectedCollectionId = item.relationId
        const result = await catalogQuery({
          query: GET_RELEASE_ITEM_COLLECTIONS,
          variables: {
            where: { id: { equals: item.relationId } },
            orderBy: { releaseDate: OrderDirection.Asc },
          },
        })
        const collectionItems = result.data?.releaseItemCollections || []
        if (collectionItems.length === 1) {
          bulkEditState.preselectedVersions = collectionItems[0]?.releaseItems?.map((volume) =>
            getVersionFromVolume(volume),
          ) as string[]
        }
      }
    })
    .with(NoveltyRelationTypeType.Volume, P.nullish, async () => {
      bulkEditState.defaultSelectedTab = 'volume'
      if (add_action) {
        bulkEditState.preselectedVersions = [item.version]
      }
    })
    .with(NoveltyRelationTypeType.Extra, async () => {})
    .exhaustive()
  bulkEditState.visible = true
}

const onScroll = (event: Event) => {
  const target = event.target as HTMLElement
  scrollY.value = target.scrollTop
  scrollGrip.onScroll()
}

watch([width], () => {
  screenWidth.value = window.innerWidth
  screenHeight.value = window.innerHeight

  // Calculate image height based on grid
  const columns = responsive(screenWidth.value, 2, 2, 3, 5)
  const newWidth =
    (screenWidth.value -
      currentTheme.value.layout.appPadding * 2 -
      (columns - 1) * currentTheme.value.layout.releaseItemGapHorizontal) /
    columns
  imageHeight.value = (newWidth * 3) / 2
})

function toggleOwned(item: PreviewEntryData, owned: boolean) {
  match(item.relationType)
    .with(NoveltyRelationTypeType.Volume, P.nullish, () => {
      if (!item.version) {
        console.error('Cannot toggle owned for item without number', item)
        return
      }
      updateOwned(item.entryId, item.version, Number(!owned))
    })
    .with(NoveltyRelationTypeType.Collection, async () => {
      if (!owned) {
        await addVolumesFromCollectionToOwnedMap(
          item,
          collectionMap.value[item.entryId]?.ownedMap,
          t,
          updateOwned,
          updateOwnedMap,
        )
      }

      updateMiscOwned(
        item.entryId,
        item.relationId,
        UserMiscPossessionActivityTypeType.Collection,
        Number(!owned),
        { suppressSiblingToast: noveltyAddMode.value },
      )
      return
    })
    .with(NoveltyRelationTypeType.Extra, () => {
      console.warn('Toggling owned is not supported for extras', item)
      return
    })
}

function toggleRead(item: PreviewEntryData, read: boolean) {
  if (!item.version) {
    console.error('Cannot toggle read for item without number', item)
    return
  }
  updateLatestRead(item.entryId, item.version, !read)
}

function onIncreaseOwned(item: PreviewEntryData) {
  const versions = getLatestNotOwnedVolumeVersions(collectionMap.value, item.entryId)
  match(versions?.length as number | undefined)
    .with(P.union(0, undefined), () => {
      console.error('Cannot increase owned volume', versions)
    })
    .with(1, () => {
      updateOwned(item.entryId, versions![0]!, 1)
    })
    .otherwise(() => {
      const volumeNumber = getVolumeNumberFromVersion(versions![0]!)
      openOptionsSelect({
        label: {
          key: 'ambiguousVolumeIncreaseDetected',
          context: { number: formatVolumeNumber(volumeNumber) },
        },
        options: versions!.map((n) => ({
          translatedLabel:
            getVolumeSuffixFromId(getSuffixFromVolumeVersion(n)).name || t('defaultVolumeSuffix'),
          value: n,
        })),
        selectedValue: volumeNumber,
        onSave: (selected) => {
          updateOwned(item.entryId, selected, 1)
        },
      })
    })
}

function onIncreaseRead(item: PreviewEntryData) {
  const no = getLatestNotReadVolumeNo(collectionMap.value, item.entryId)
  if (no == null) {
    console.error('Cannot increase read volume', no)
    return
  }
  updateLatestRead(item.entryId, no, true)
}

function onRefresh() {
  resetPagination({
    ...getWhere.value(releaseItemFilter.value),
    ...getSort.value(sortParams.value),
  })
}

async function scrollToAndPaginate(
  top: number,
  targetSkip?: number,
  externalEl?: HTMLElement,
): Promise<number> {
  if (!listRef.value) return 0
  while (!firstLoadDone.value || isLoading.value) {
    await new Promise((r) => requestAnimationFrame(r))
  }
  await nextTick()
  restoringScroll.value = true
  const el = externalEl ?? listRef.value
  try {
    if (targetSkip !== undefined) {
      await loadUntilSkip(targetSkip)
      await nextTick()
    }
    el.scrollTop = top
  } finally {
    restoringScroll.value = false
  }
  if (!externalEl) {
    scrollY.value = el.scrollTop
    scrollGrip.recalculate()
    scrollGrip.onScroll()
  }
  return el.scrollTop
}

defineExpose({
  scrollTop: computed(() => scrollY.value),
  currentSkip,
  scrollToAndPaginate,
  resetPagination: () => {
    resetPagination({
      ...getWhere.value(releaseItemFilter.value),
      ...getSort.value(sortParams.value),
    })
  },
})
</script>

<style scoped>
.preview-list {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.preview-list::-webkit-scrollbar {
  display: none;
}
</style>
